import {Controller} from "stimulus";

// Copies the default dates of the chosen bill run picker card (its radio's
// data-period-start, -period-end, -bill-date and -due-date) into the date
// fields, so staff start from the chosen cycle's next period and can still
// change any date. A blank default (a manual cycle's bill date) clears the
// field for staff to fill.
// Once a card is chosen (or the server re-renders the form with one
// checked), a date that differs from that card's default is marked: its
// field wrapper gets data-changed, which the view styles, and its "Changed
// from" note shows the default with a Reset button that puts it back. The
// note's id joins the input's aria-describedby only while the note shows, so
// an unchanged date is not described by an empty hidden note; any help text
// id stays.
// Choosing another card clears the marks. The period's length in days, both
// ends counted, is shown in the periodLength target.
export default class extends Controller {
    static targets = ["periodStart", "periodEnd", "billDate", "dueDate", "field", "periodLength"];

    connect() {
        const checked = this.element.querySelector("input[type=radio][data-period-start]:checked");
        this.defaults = checked ? this.defaultsFrom(checked) : null;
        this.refresh();
    }

    choose(event) {
        this.defaults = this.defaultsFrom(event.target);
        Object.entries(this.defaults).forEach(([name, date]) => {
            const input = this.input(name);
            if (input) input.value = date;
        });
        this.refresh();
    }

    reset(event) {
        const name = event.currentTarget.dataset.field;
        const input = this.input(name);
        input.value = this.defaults[name];
        this.refresh();
        input.focus();
    }

    refresh() {
        this.fieldTargets.forEach((field) => this.mark(field));
        this.showPeriodLength();
    }

    // private

    defaultsFrom(radio) {
        const dates = radio.dataset;
        return {
            periodStart: dates.periodStart || "",
            periodEnd: dates.periodEnd || "",
            billDate: dates.billDate || "",
            dueDate: dates.dueDate || ""
        };
    }

    input(name) {
        const key = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`;
        return this[key] ? this[`${name}Target`] : null;
    }

    mark(field) {
        const name = field.dataset.field;
        const input = this.input(name);
        const note = field.querySelector("[data-role='changed-note']");
        const changed = Boolean(this.defaults && input && input.value !== this.defaults[name]);

        if (changed) {
            field.setAttribute("data-changed", "");
        } else {
            field.removeAttribute("data-changed");
        }
        if (!note) return;

        note.hidden = !changed;
        note.querySelector("[data-role='default-date']").textContent = changed ? this.formatDate(this.defaults[name]) : "";
        if (input) this.describeBy(input, note.id, changed);
    }

    describeBy(input, id, include) {
        const ids = (input.getAttribute("aria-describedby") || "").split(/\s+/).filter((existing) => existing && existing !== id);
        if (include) ids.push(id);

        if (ids.length) {
            input.setAttribute("aria-describedby", ids.join(" "));
        } else {
            input.removeAttribute("aria-describedby");
        }
    }

    // "2026-09-01" as "09/01/2026", the way the date inputs show it; a blank default reads "blank".
    formatDate(iso) {
        if (!iso) return "blank";

        const [year, month, day] = iso.split("-");
        return `${month}/${day}/${year}`;
    }

    showPeriodLength() {
        if (!this.hasPeriodLengthTarget) return;

        this.periodLengthTarget.textContent = this.periodLengthText();
    }

    periodLengthText() {
        if (!this.hasPeriodStartTarget || !this.hasPeriodEndTarget) return "";

        const start = Date.parse(this.periodStartTarget.value);
        const end = Date.parse(this.periodEndTarget.value);
        if (Number.isNaN(start) || Number.isNaN(end)) return "";
        if (end < start) return "The period ends before it starts.";

        const days = Math.round((end - start) / 86400000) + 1;
        return `${days} ${days === 1 ? "day" : "days"}`;
    }
}
